import React, { useEffect, useState } from 'react';
import { Select, Button, Row, Col, Form, Divider, TreeSelect, Popconfirm, notification } from 'antd';
import BreadcrumbComponent from '../../../../components/Breadcrumb';
import { IBreadcrumbItem } from '../../../../models/breadcrumb';
import { useTranslation } from 'react-i18next';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { convertToTreeData } from '../../../../utils/commonFunctions';
import { RootState, useAppDispatch } from '../../../../app/store';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchIndicatorsMinistriesMappingList, mapIndicatorsMinistry } from '../indicatorsMappingApi';
import { fetchIndicators, fetchMinistries } from '../../../master/masterApi';

const { Option } = Select;

interface AddUpdateIndicatorMappingProps {

    isEdit: boolean;
}

const AddUpdateIndicatorMapping: React.FC<AddUpdateIndicatorMappingProps> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();

    const { ministryId } = useParams<{ ministryId: string }>();

    const { ministryIndicators } = useSelector((state: RootState) => state.indicatorsMapping);
    const { indicators, ministries } = useSelector((state: RootState) => state.masterData);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    const [isFormDirty, setIsFormDirty] = useState(false);

    useEffect(() => {
        dispatch(fetchIndicatorsMinistriesMappingList());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchMinistries());
    }, [dispatch]);
    // useEffect(() => {
    //     if (ministryId) {
    //         updateIndicators(parseInt(ministryId));

    //     }
    // }, [dispatch]);
    useEffect(() => {
        if (!(indicators?.length)) {
            dispatch(fetchIndicators());
        }
        if (ministryId) {
            updateIndicators(parseInt(ministryId));

        }
    }, [ministryIndicators]);

    const breadcrumbItems: Array<IBreadcrumbItem> = [
        { title: t('home'), path: '/home' },
        {
            title: t('mapping-indicator.ministries-departments'),
            onClick: () => navigate('/setup/indicators-mapping') // Call the onCancel function when clicked
        },
        {

            title: props.isEdit ? t('mapping.edit.button') : t('mapping.add.button')
        }
    ];

    const treeData = convertToTreeData(ministries);

    // Function to get indicators by ministry id
    const getIndicatorsByMinistryId = (ministryId: number): number[] => {
        console.log('ministryIndicators', ministryIndicators);
        const ministry = ministryIndicators.find((ministry) => ministry.id === ministryId);
        if (!ministry) {
            return [];
        }
        return ministry.indicators.map((indicator: any) => indicator.id);
    };

    //Update indicators binding on the change of ministry
    const updateIndicators = (ministryId: number) => {
        if (ministryId) {
            const indicators = getIndicatorsByMinistryId(ministryId);
            form.setFieldsValue({
                ministry: ministryId,
                indicators: indicators
            });
        }
    }
    const onFormValuesChange = (changedValues: any) => {
        setIsFormDirty(form.isFieldsTouched());
    };
    //On load check ministry id and bind indicators accordingly


    const onFinish = (values: any) => {
        const indicatorsMinistryMapping = {
            nodeId: values.ministry,
            indicatorsId: values.indicators
        };

        dispatch(mapIndicatorsMinistry(indicatorsMinistryMapping))
            .unwrap()
            .then((response) => {

                form.resetFields();
                if (response?.status === 'success') {
                    notification.success({ message: t('success'), description: response?.message })
                }

                navigate('/setup/indicators-mapping')
            });
    };

    const expandAllKeys = (nodes: any) => {
        const keys: string[] = [];
        const traverse = (nodeList: any) => {
            nodeList.forEach((node: any) => {
                keys.push(node.key);
                if (node.children) {
                    traverse(node.children);
                }
            });
        };
        traverse(nodes);
        return keys;
    };

    return (

        <Row className='mainTextArea' style={{ width: '100%' }}>

            <Col xs={24} sm={24} md={24} lg={24} xl={24} >
                <Row gutter={[16, 16]} style={{ borderBottom: '1px solid #CCC', marginBottom: '10px', paddingBottom: '10px' }}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} >
                        <h4>{t('mapping-indicator.ministries-departments')}</h4>
                        <BreadcrumbComponent items={breadcrumbItems} separator='>' />
                    </Col>
                </Row>
            </Col>

            <Form
                style={{ width: '100%' }}
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onValuesChange={onFormValuesChange}
            >
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={t('mapping-indicator.ministry-department')}
                            name="ministry"
                            rules={[{ required: true, message: t('mapping-indicator.ministry-department.req.error') }]}>
                            <TreeSelect
                                disabled={ministryId ? true : false}
                                treeData={treeData}
                                onChange={(value) => updateIndicators(parseInt(value))}
                                placeholder={t('user-management.select-ministry')}
                                allowClear
                                showSearch
                                filterTreeNode={(input, treeNode) => {
                                    const title = treeNode?.title;
                                    if (typeof title === 'string') {
                                        return title.toLowerCase().includes(input.toLowerCase());
                                    }
                                    return false;
                                }}
                                onSearch={(value) => {
                                    if (value) {
                                        setExpandedKeys(expandAllKeys(treeData)); // Expand all nodes during search
                                    } else {
                                        setExpandedKeys([]); // Collapse all nodes when search is cleared
                                    }
                                }}
                                onDropdownVisibleChange={(open) => {
                                    if (!open) {
                                        setExpandedKeys([]); // Collapse tree when dropdown is closed
                                    }
                                }}
                                treeExpandedKeys={expandedKeys}
                                onTreeExpand={(keys) => setExpandedKeys(keys.map(String))} // Cast keys to strings

                            />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            label={t('mapping-indicator.ministry-indicator')}
                            name="indicators"
                            className='dropdown'
                            rules={[{ required: true, message: t('mapping-indicator.select.indicators.req.error') }]}>
                            <Select
                                showSearch={true}
                                filterOption={(input, option) => {
                                    const children = option?.children as string | undefined;
                                    return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                                }}
                                allowClear
                                placeholder={t('mapping-indicator.select.indicators')} mode="multiple" className='indicator_list'>
                                {indicators.map(indicator => (
                                    <Option key={indicator.id} value={indicator.id}>
                                        {indicator.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Divider style={{ margin: '15px 0 25px 0', borderBottom: '1px solid #CCC' }}></Divider>
                    <Col span={24} className='actionButHolder'>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" icon={<i className="fa fa-save" />}
                                disabled={!isFormDirty}
                            >
                                <PlusOutlined /> {t('btn.save')}
                            </Button>
                            {isFormDirty ?
                                <Popconfirm
                                    title={t('confirmation')}
                                    description={t('cancel.confirmation.msg')}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                    onConfirm={() => navigate('/setup/indicators-mapping')}>
                                    <Button type="primary" ><CloseOutlined /> {t('btn.cancel')}</Button>
                                </Popconfirm>
                                :
                                <Button type="primary" onClick={() => navigate('/setup/indicators-mapping')} icon={<i className="fa fa-times" />}>
                                    <CloseOutlined /> {t('btn.cancel')}
                                </Button>
                            }
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

        </Row >
    );
};

export default AddUpdateIndicatorMapping;